import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BrandLogo } from '../components/BrandLogo';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            const user = await login(email, password);
            toast.success(`Welcome back, ${user.full_name}!`);
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || 'Login failed. Please try again.';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex" data-testid="login-page">
            {/* Left side - Form */}
            <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12 xl:px-24">
                <div className="w-full max-w-md mx-auto">
                    {/* Logo */}
                    <BrandLogo
                        className="auth-brand mb-12"
                        imgClassName="auth-brand-wordmark"
                        showSubtitle
                        subtitleClassName="auth-brand-subtitle"
                    />

                    {/* Welcome text */}
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold font-['Manrope'] text-slate-900 mb-2">
                            Welcome back
                        </h2>
                        <p className="text-slate-600">
                            Sign in to continue to your dashboard
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <Label htmlFor="email" className="text-slate-700">Email address</Label>
                            <div className="relative mt-2">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@university.edu"
                                    className="pl-10 h-12"
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="password" className="text-slate-700">Password</Label>
                            <div className="relative mt-2">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    className="pl-10 pr-10 h-12"
                                    required
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    data-testid="toggle-password-btn"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" className="rounded border-slate-300 text-[#2F8FCB] focus:ring-[#2F8FCB]" />
                                <span className="text-sm text-slate-600">Remember me</span>
                            </label>
                            <a href="#" className="text-sm font-medium text-[#2F8FCB] hover:text-[#0F2A44]">
                                Forgot password?
                            </a>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 bg-[#0F2A44] hover:bg-[#1E3A8A] text-white rounded-full font-medium btn-transition"
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    Sign in
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </Button>
                    </form>

                    {/* Register link */}
                    <p className="mt-8 text-center text-slate-600">
                        Don't have an account?{' '}
                        <Link
                            to="/register"
                            className="font-medium text-[#2F8FCB] hover:text-[#0F2A44]"
                            data-testid="register-link"
                        >
                            Create account
                        </Link>
                    </p>
                </div>
            </div>

            {/* Right side - Image */}
            <div className="hidden lg:flex lg:flex-1 relative bg-[#0F2A44]">
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ backgroundImage: 'url(https://images.pexels.com/photos/159775/library-la-trobe-study-students-159775.jpeg)' }}
                />
                <div className="relative z-10 flex flex-col justify-end p-12 text-white">
                    <div className="max-w-lg">
                        <h3 className="text-3xl font-bold font-['Manrope'] mb-4">
                            Master Real-World Accounting
                        </h3>
                        <p className="text-lg text-white/80 leading-relaxed">
                            Practice journal entries, build trial balances, and create financial statements 
                            in a simulated business environment aligned with CHED standards.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
